import { ChangeEvent, FC, useEffect, useState } from 'react';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { useParams } from 'react-router-dom';

import { showDialog } from 'store/reducers/dialogsSlice';
import {
  errorBuilderPrivateDialog,
  errorBuilderPublishDialog,
  successBuilderDialog,
  notifyMessages,
} from 'utilities';
import { useRedirectAndScroll, useAppDispatch, useAppSelector, useQuilt } from 'hooks/index';
import { Loader } from 'components/Loader/Loader';
import { DefaultDialog } from 'components/DefaultDialog';
import { setIsSave, setOpen } from 'store/reducers/notifySlice';

import styles from './styles.module.scss';
import { isVac } from 'utilities/is-vac';
import { Helmet } from 'react-helmet-async';
import { CreateMemorialSteps } from '../CreateMemorialSteps';
import { publicPrivate, updateQuiltLanguage } from 'api/http-client/queries/quilts';
import { Language } from 'providers/translates/enums';
import { useTranslates } from 'providers/translates';
import { setEditableQuiltState } from 'store/reducers/quiltSlice';
import MarkdownRenderer from 'components/MarkdownRenderer';
import { TributesSection } from '../TributesSection';

interface CreateMemorialQuiltStepFourProps {
  isActiveStep: number;
  steps: {
    index: number;
    title: string;
  }[];
}

const translationsHelmet = {
  titleEN: 'Saluting Service - Create a quilt square - Step 4',
  titleFR: 'Saluons leur service - Créer un carreau de courtepointe - Étape 4'
} as const;

export const CreateMemorialQuiltStepFour: FC<CreateMemorialQuiltStepFourProps> = ({
  isActiveStep,
  steps
}) => {
  const { translates: t, selectedLang } = useTranslates();

  const displaySettings = t?.quiltBuilderStepFour?.displaySettings;
  const sharingInstructions = t?.quiltBuilderStepFour?.sharingInstructions;
  const managePreferences = t?.quiltBuilderStepFour?.managePreferences;
  const privateDialog = t?.quiltBuilderStepFour?.privateDialog;
  const successDialog = t?.quiltBuilderStepFour?.successDialog;
  const requiredMainPhotoDialog = t?.quiltBuilderStepFour?.requiredMainPhotoDialog;
  const builderHeader = t?.quiltBuilder?.builderHeader;

  const { id } = useParams();
  const dispatch = useAppDispatch();
  const editableQuiltStore = useAppSelector((state) => state.quilt.editableQuilt);
  const { redirectAndScroll } = useRedirectAndScroll({});
  const { publish, loading, setLoading } = useQuilt({ id: id || '' });

  const isFrench = selectedLang === Language.FRENCH;

  const [typeMemorialPublish, setTypeMemorialPublish] = useState('public');
  const [memorialLanguage, setMemorialLanguage] = useState<string>(editableQuiltStore.lang ?? Language.ENGLISH);
  const [errorPublishDialog, setErrorPublishDialog] = useState(false);
  const [errorPrivateDialog, setErrorPrivateDialog] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const handleChangeTypeMemorial = async (event: ChangeEvent<HTMLInputElement>) => {
    const isTypeMemorialPublish = event.target.value === 'public';
    const { finalImage } = editableQuiltStore;

    if (!finalImage && isTypeMemorialPublish) {
      dispatch(setOpen(requiredMainPhotoDialog?.text ?? notifyMessages.quiltPublicImageError));
      return;
    }

    if (id) {
      setLoading(true);
      try {
        await publicPrivate(id, !isTypeMemorialPublish);
        dispatch(setEditableQuiltState({ ...editableQuiltStore, isPublic: isTypeMemorialPublish }));
      } catch (err) {
        console.error(err)
      }
      setLoading(false);
    }

    setTypeMemorialPublish(event.target.value);
    if (!isTypeMemorialPublish) {
      setErrorPrivateDialog(true);
    }
  }

  const handleChangeLanguageMemorial = async (event: ChangeEvent<HTMLInputElement>) => {
    const language = event.target.value;

    if (id) {
      try {
        setLoading(true);
        const newQuilt = { 
          ...editableQuiltStore, 
          lang: language
        };
        await updateQuiltLanguage(
          id, 
          language
        );
        dispatch(setEditableQuiltState(newQuilt));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    setMemorialLanguage(language);
  }

  useEffect(() => {
    // need for correct moving from first step or third
    dispatch(setIsSave(true));
  }, [dispatch]);

  useEffect(() => {
    setMemorialLanguage(editableQuiltStore.lang ?? Language.ENGLISH);
  }, [editableQuiltStore.lang]);

  useEffect(() => {
    if (typeof editableQuiltStore?.isPublic !== 'boolean') {
      setTypeMemorialPublish('public');
      return;
    }

    setTypeMemorialPublish(editableQuiltStore?.isPublic ? 'public' : 'private');
  }, [editableQuiltStore?.isPublic]);

  useEffect(() => {
    if (!isMounted || !id) {
      setIsMounted(true);
      return;
    }
  }, [typeMemorialPublish, editableQuiltStore?.isPublic, isMounted, id]);

  const showDialogBlocks = () => {
    dispatch(showDialog('blocks'));
  };

  const goToMyMemorial = () => {
    id && redirectAndScroll(`/quilts/${id}/memorial`);
  };

  const goToProfileUser = () => {
    redirectAndScroll(`/profile`);
  };

  const onPublish = () => {
    const isTypeMemorialPublish = typeMemorialPublish === 'public';
    const { finalImage } = editableQuiltStore;

    if (!finalImage && isTypeMemorialPublish) {
      dispatch(setOpen(requiredMainPhotoDialog?.text ?? notifyMessages.quiltPublicImageError));
      return;
    }

    publish.onPublish(isTypeMemorialPublish);
  };

  const handleViewing = () => {
    if (
      !!editableQuiltStore?.finalImage &&
      editableQuiltStore.isPublic &&
      editableQuiltStore.isPublished
    ) {
      redirectAndScroll(`/?visible=${id}`);
    } else {
      dispatch(setOpen(t?.quiltBuilder?.viewOnHomePageNotAvailablePopUp?.viewOnHomePageNotAvailableText ?? notifyMessages.quiltViewHomePageError));
    }
  };

  return (
    <>
    {isVac() && (
      <Helmet>
        <title>{isFrench ? translationsHelmet?.titleFR : translationsHelmet?.titleEN}</title>
      </Helmet>
    )}
    <div className={`center ${styles.block}`} id="fourStep">
      <div className={styles.settings}>
        <div className={styles.titleQuilt}>
          <span>
            {builderHeader?.introSubheading ?? 'Memorial quilt square for '}
          </span>
          <span>
            {editableQuiltStore?.firstName || ''} {editableQuiltStore?.lastName || ''}
          </span>
        </div>
        <TributesSection setLoading={setLoading} />
        <div className={styles.title}>
          <span className="defaultTitle">{displaySettings?.displaySettingsHeading ?? 'Display settings'}</span>
          <span className="defaultText">
            {displaySettings?.displaySettingsInstructions ? (
              <MarkdownRenderer 
                content={displaySettings?.displaySettingsInstructions}
              />
            ) : (
              <>
                Read the descriptions below and choose how you want to share your memorial quilt square.
                You can make changes later in the “
                <span onClick={goToProfileUser} className={styles.settings__user}>
                  My Quilts
                </span>
                ” section of “My Account.”
              </>
            )}
          </span>
        </div>
        <div className={styles.title}>
          <RadioGroup
            defaultValue="public"
            onChange={handleChangeTypeMemorial}
            value={typeMemorialPublish}
          >
            <FormControlLabel
              sx={{ alignItems: 'start' }}
              value="public"
              control={<Radio />}
              componentsProps={{
                typography: {
                  color: '#333333',
                  fontSize: { xs: '18px', sm: '22px' },
                  fontFamily: 'Open Sans;',
                },
              }}
              label={
                <p className='colorNavy'>
                  {displaySettings?.publicDisplayRadioButtonText ? (
                    <MarkdownRenderer 
                      content={displaySettings?.publicDisplayRadioButtonText}
                    />
                  ) : (
                    <>
                      <b>Share publicly on the Canadian Healing Quilt.</b>
                      <span>
                        You will receive a link (by email) that you can share with others. Visitors can
                        find it by searching by name. Anyone can see the photos and information you have
                        shared.
                      </span>
                    </>
                  )}
                </p>
              }
            />

            <FormControlLabel
              sx={{ alignItems: 'start' }}
              value="private"
              control={<Radio />}
              componentsProps={{
                typography: {
                  color: '#333333',
                  fontSize: { xs: '18px', sm: '22px' },
                  fontFamily: 'Open Sans;',
                },
              }}
              label={
                <p className='colorNavy'>
                  {displaySettings?.privateDisplayRadioButtonText ? (
                    <MarkdownRenderer 
                      content={displaySettings?.privateDisplayRadioButtonText}
                    />
                  ) : (
                    <>
                      <b>Keep private. </b>
                      <span>
                        You will receive a link (by email) that you can share. Only people with the link
                        will be able to see the photos and information you have shared. Your quilt
                        square will NOT be visible or searchable in the Canadian Healing Quilt.
                      </span>
                    </>
                  )}
                </p>
              }
            />
          </RadioGroup>
        </div>
        {isVac() && (
          <>
            <div className={styles.title}>
              <span className="defaultTitle1">{displaySettings?.languageSettingsHeading ?? 'Language'}</span>
              <span className="defaultText">
                {displaySettings?.languageSettingsInstructions ?? (
                  <>
                    loren ipsum loren ipsum loren ipsum loren ipsum loren ipsum loren ipsum loren ipsum loren ipsum loren ipsum loren ipsum
                  </>
                )}
              </span>
            </div>
            <div className={styles.title}>
              <RadioGroup
                onChange={handleChangeLanguageMemorial}
                value={memorialLanguage}
              >
                <FormControlLabel
                  sx={{ alignItems: 'start' }}
                  value={Language.ENGLISH}
                  control={<Radio />}
                  componentsProps={{
                    typography: {
                      color: '#333333',
                      fontSize: { xs: '18px', sm: '22px' },
                      fontFamily: 'Open Sans;',
                    },
                  }}
                  label={
                    <p className='colorNavy'>
                      {displaySettings?.englishDisplayRadioButtonText ? (
                        <MarkdownRenderer 
                          content={displaySettings?.englishDisplayRadioButtonText}
                        />
                      ) : (
                        <b>Publish my quilt in English</b>
                      )}
                    </p>
                  }
                />

                <FormControlLabel
                  sx={{ alignItems: 'start' }}
                  value={Language.FRENCH}
                  control={<Radio />}
                  componentsProps={{
                    typography: {
                      color: '#333333',
                      fontSize: { xs: '18px', sm: '22px' },
                      fontFamily: 'Open Sans;',
                    },
                  }}
                  label={
                    <p className='colorNavy'>
                      {displaySettings?.frenchDisplayRadioButtonText ? (
                        <MarkdownRenderer 
                          content={displaySettings?.frenchDisplayRadioButtonText}
                        />
                      ) : (
                        <b>Publish my quilt in French</b>
                      )}
                    </p>
                  }
                />
              </RadioGroup>
            </div>
          </>
        )}

        <span className="defaultText">
          {displaySettings?.clickPublishToSaveInstructionText ?? 'Click PUBLISH to save these settings and publish your quilt square.'}
        </span>

        <div className={styles.displayActions}>
          <button className={`customButton`} onClick={showDialogBlocks}>
            {displaySettings?.previewButtonLabel ?? 'Preview'}
          </button>

          <div className={styles.displayActions__buttonText}>
            <span className='colorNavy'>{displaySettings?.finishedText ?? `I’m finished...`}</span>
            <button className={`customButton`} onClick={onPublish}>
              {displaySettings?.publishButtonLabel ?? 'Publish'}
            </button>
          </div>
        </div>

        <div className={styles.info}>
          <div className={styles.title}>
            <span className="defaultTitle">
              {sharingInstructions?.sharingInstructionsHeader ?? 'How to share your memorial quilt square with others'}
            </span>
            <span className="defaultText">
              {sharingInstructions?.sharingInstructionsText ?? (
                <>
                  You can share on social media or by email using the icons on the right edge of your
                  published memorial quilt square.
                </>
              )}
            </span>
          </div>

          <img src="/img/general/sharing-picture-1.png" alt="sharing-1" />
        </div>

        <div className={styles.info}>
          <div className={styles.title}>
            <span className="defaultTitle">{managePreferences?.managePreferencesHeading ?? 'Manage preferences'}</span>
            <span className="defaultText">
              {managePreferences?.managePreferencesText ? (
                <div 
                  dangerouslySetInnerHTML={{__html: managePreferences?.managePreferencesText}}
                />
              ) : (
                <>
                  To update your profile information and see other quilts you can edit, go to "
                  <span onClick={goToProfileUser} className={styles.settings__user}>
                    My Account
                  </span>
                  ."
                </>
              )}
            </span>
          </div>
        </div>

        <div className="delimitierGrey"></div>

        <div className={styles.actions}>
          <div className={styles.actions__right}>
            <button className={`customButton`} onClick={handleViewing}>
              {t?.quiltBuilder?.builderFooter?.viewOnHomePageButtonLabel ?? 'View Quilt on Home Page'}
            </button>
            <button className={`customButton`} onClick={goToMyMemorial}>
              {t?.quiltBuilder?.builderFooter?.viewMyMemorialButtonLabel ?? 'VIEW MY MEMORIAL'}
            </button>
          </div>
          <div className={styles.actions__left}>
            <CreateMemorialSteps 
              isActiveStep={isActiveStep} 
              steps={steps} 
            />
          </div>
        </div>
      </div>

      <Loader loading={loading} />
      <DefaultDialog
        open={publish.successPublish}
        setDialog={publish.setSuccessPublish}
        title={successDialog?.title ?? successBuilderDialog.title}
        text={successDialog?.text ?? successBuilderDialog.text}
        noCancelBtn
        confirmBtnText={successDialog?.confirmBtnText ?? "Close"}
        confirmAction={() => publish.setSuccessPublish(false)}
      />
      <DefaultDialog
        open={errorPrivateDialog}
        setDialog={setErrorPrivateDialog}
        title={privateDialog?.title ?? errorBuilderPrivateDialog.title}
        text={privateDialog?.text ?? errorBuilderPrivateDialog.text}
        noCancelBtn
        confirmBtnText={privateDialog?.confirmBtnText ?? "OK"}
        confirmAction={() => setErrorPrivateDialog(false)}
      />
      <DefaultDialog
        open={errorPublishDialog}
        setDialog={setErrorPublishDialog}
        title={errorBuilderPublishDialog.title}
        text={errorBuilderPublishDialog.text}
        noCancelBtn
        confirmBtnText="OK"
        confirmAction={() => setErrorPublishDialog(false)}
      />
    </div>    
    </>
  );
};
